import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import MediaCard from "@/components/MediaCard";
import LiveCarousel from "@/components/LiveCarousel";
import { useAuth } from "@/context/AuthContext";
import { Tv, Radio, Flame, Sparkles, Zap, MapPin, ArrowRight, PlusCircle, BadgeCheck } from "lucide-react";

function Rail({ title, icon: Icon, items, accent = "text-primary", testid }) {
  if (!items || items.length === 0) return null;
  return (
    <section className="mt-9" data-testid={testid}>
      <div className="flex items-center gap-2 mb-4">
        <Icon className={accent} size={20} />
        <h2 className="font-heading font-bold text-xl sm:text-2xl tracking-tight">{title}</h2>
      </div>
      <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
        {items.map((m, i) => (
          <div key={m.id} className="w-64 sm:w-72 shrink-0">
            <MediaCard media={m} index={i} />
          </div>
        ))}
      </div>
    </section>
  );
}

export default function Home() {
  const [data, setData] = useState(null);
  const { user } = useAuth();
  const { t } = useTranslation();

  useEffect(() => {
    api.get("/home").then((r) => setData(r.data)).catch(() => {});
  }, [user]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <LiveCarousel />

      <div className="mt-8" />
      <Rail title={t("home.live_now")} icon={Zap} items={data?.live} accent="text-rdcred" testid="rail-live" />

      {/* Chaînes certifiées — mise en avant */}
      {data?.certified?.length > 0 && (
        <section className="mt-9" data-testid="rail-certified">
          <div className="flex items-center gap-2 mb-4">
            <BadgeCheck className="text-rdcgold" size={20} />
            <h2 className="font-heading font-bold text-xl sm:text-2xl tracking-tight">{t("home.certified")}</h2>
            <Link to="/browse?certified=true" data-testid="rail-certified-see-all"
              className="ml-auto inline-flex items-center gap-1 text-sm font-semibold text-rdcgold hover:underline">
              {t("home.certified_see_all")} <ArrowRight size={15} />
            </Link>
          </div>
          <div className="flex gap-4 overflow-x-auto no-scrollbar pb-2 -mx-4 px-4 sm:mx-0 sm:px-0">
            {data.certified.map((m, i) => (
              <div key={m.id} className="w-64 sm:w-72 shrink-0">
                <MediaCard media={m} index={i} />
              </div>
            ))}
          </div>
        </section>
      )}

      {user && data?.my_province?.length > 0 && (
        <Rail title={t("home.my_province", { name: data.my_province_name })} icon={MapPin} items={data.my_province} accent="text-rdcblue" testid="rail-my-province" />
      )}
      <Rail title={t("home.popular_tv")} icon={Tv} items={data?.popular_tv} accent="text-rdcblue" testid="rail-popular-tv" />
      <Rail title={t("home.popular_radio")} icon={Radio} items={data?.popular_radio} accent="text-rdcgold" testid="rail-popular-radio" />
      <Rail title={t("home.most_viewed")} icon={Flame} items={data?.most_viewed} accent="text-rdcred" testid="rail-most-viewed" />
      <Rail title={t("home.new_media")} icon={Sparkles} items={data?.new_media} accent="text-primary" testid="rail-new" />

      {/* CTA inscrire */}
      <section className="my-12 rounded-3xl border border-border bg-card p-8 sm:p-10 flex flex-col sm:flex-row items-center gap-6 justify-between">
        <div>
          <h3 className="font-heading font-bold text-xl sm:text-2xl">{t("home.cta_title")}</h3>
          <p className="text-muted-foreground text-sm mt-1">{t("home.cta_sub")}</p>
        </div>
        <Link to="/register-media" data-testid="cta-register-media" className="flex items-center gap-2 rounded-full bg-primary text-white px-6 py-3 font-semibold hover:bg-rdcblue transition-colors shrink-0">
          <PlusCircle size={18} /> {t("home.cta_btn")} <ArrowRight size={16} />
        </Link>
      </section>
    </div>
  );
}
